import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeActionMediaViewSettings} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionCtaButtonsResetSettingsForLock, DoppeLinkSpecEntityType, DoppeLinkSpecType
} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeDtoEvent} from '../../client-server-common/types/doppe-dto-event';
import type {
    BymoPagePropsContentSettingsMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';
import {
    DoppeExternalEventProvider,
    doppeExternalEventProviderResolveBusinessEntityReference
} from '../../client-server-common';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';
import type {CurrencyCode, RichTextContent, HideableValue, SerializedDateTimeValueAsString } from '@wix/devzai-utils-common';
import {arrayRemoveNullValues, dateResolveFromDateTimeValue, dateSerializedToString, hideableValueCreateHidden } from '@wix/devzai-utils-common';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';

export namespace DoppeActionTypeHoppEvent {

    export interface BaseActionSettings extends DoppeActionMediaViewSettings {
        eventPageLink: string;
        eventTitle: HideableValue<string>;
        description: HideableValue<RichTextContent>;
        eventImages: WixMediaResource[] | null;
        eventStartDate: HideableValue<SerializedDateTimeValueAsString>;
        eventEndDate: HideableValue<SerializedDateTimeValueAsString>;
        /** Event time zone ID in TZ database format (e.g., "America/New_York"). Null when not available from the source. When visible (not hidden), the time zone abbreviation is shown with the date. */
        timeZoneId: HideableValue<string | null>;
        location: HideableValue<string>;
        priceData: HideableValue<DoppeActionPrice>;
        couponCode: HideableValue<string>;
        ribbon: HideableValue<string>;
        eventProvider: DoppeExternalEventProvider | null;
        eventProviderId: string | null;
        /** Whether the event date is TBD (start date is null from the external source). */
        isDateTBD: boolean;
    }

    export interface ActionSettings extends BaseActionSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
    }
}

export const doppeActionTypeHoppEventActionContentSettingsMetadata = {
    eventPageLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
    eventTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    eventImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    eventStartDate: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    eventEndDate: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    location: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    priceData: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
    ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeHoppEvent.ActionSettings>

export const doppeActionTypeHoppEvent = doppeActionTypeDefineMetadata<DoppeActionTypeHoppEvent.ActionSettings>({
    id: 'io.bymo.action/hopp-event',
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveActionSettings: action => {
        const {...restSettings} = action.settings;
        return {
            eventPageLink: '',
            eventTitle: hideableValueCreateHidden(''),
            description: hideableValueCreateHidden(''),
            eventImages: null,
            eventStartDate: hideableValueCreateHidden(dateSerializedToString(new Date())),
            eventEndDate: hideableValueCreateHidden(dateSerializedToString(new Date())),
            timeZoneId: hideableValueCreateHidden(null),
            location: hideableValueCreateHidden(''),
            priceData: hideableValueCreateHidden({
                actualPrice: 0,
                currencyCode: 'USD' as CurrencyCode
            }),
            couponCode: hideableValueCreateHidden(''),
            ribbon: hideableValueCreateHidden(''),
            eventProvider: DoppeExternalEventProvider.Hopp,
            eventProviderId: null,
            isDateTBD: false,
            ...doppeActionGetMediaViewDefaultSettingsMigration(action.settings),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeHoppEventActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {customContentSectionsTypes: {}},
        hideableContentSettingsNames: {
                couponCode: true,
                description: true,
                eventStartDate: true,
                eventEndDate: true,
                eventTitle: true,
                location: true,
                priceData: true,
                ribbon: true,
                timeZoneId: true
            }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.eventProvider !== null && actionSettings.eventProviderId !== null ?
                doppeExternalEventProviderResolveBusinessEntityReference(
                    actionSettings.eventProvider,
                    actionSettings.eventProviderId
                ) : null
        ])
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: actionSettings.eventProviderId ? {
                type: DoppeLinkSpecType.Entity,
                entityType: DoppeLinkSpecEntityType.Event,
                entityId: actionSettings.eventProviderId
            } : null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
});

export function convertDoppeDtoEventToHoppEventSettings(options: {
    event: DoppeDtoEvent,
    eventSettings?: DoppeActionTypeHoppEvent.ActionSettings,
    eventProvider?: DoppeExternalEventProvider
}): DoppeActionTypeHoppEvent.ActionSettings {
    const {event, eventSettings = {}, eventProvider = DoppeExternalEventProvider.Hopp} = options;
    const extractedPrice = extractPriceFromEvent(event);

    const isDateTBD = event.eventDates?.eventStartDate === null;

    return {
        ...doppeActionContentSectionSettingsDefaultValuesMigration(eventSettings),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({ctaButtons: []}, false),
        ...eventSettings,
        ...doppeActionGetMediaViewDefaultSettingsMigration(eventSettings),
        eventPageLink: event.eventPageUrl ?? '',
        eventTitle: (event.name ?? ''),
        description: (event.description ?? ''),
        eventImages: event.media ?? [],
        eventStartDate: event.eventDates?.eventStartDate
            ? dateSerializedToString(dateResolveFromDateTimeValue(event.eventDates.eventStartDate))
            : dateSerializedToString(new Date(Date.now())),
        eventEndDate: isDateTBD
            ? hideableValueCreateHidden(dateSerializedToString(new Date(Date.now())))
            : event.eventDates?.eventEndDate
                ? dateSerializedToString(dateResolveFromDateTimeValue(event.eventDates.eventEndDate))
                : hideableValueCreateHidden(dateSerializedToString(new Date(Date.now()))),
        timeZoneId: event.eventDates?.showTimeZone
            ? (event.eventDates?.timeZoneId ?? null)
            : hideableValueCreateHidden(event.eventDates?.timeZoneId ?? null),
        location: event.location ?? '',
        priceData: extractedPrice,
        couponCode: hideableValueCreateHidden(''),
        ribbon: hideableValueCreateHidden(''),
        eventProviderId: event.id ?? null,
        eventProvider: eventProvider,
        isDateTBD: isDateTBD,
    } satisfies DoppeActionTypeHoppEvent.ActionSettings;
}

function extractPriceFromEvent(event: DoppeDtoEvent): DoppeActionPrice {
    const lowestPrice = event.lowestPrice ? parseFloat(event.lowestPrice.replace(/[^0-9.]/g, '')) : 0;
    const highestPrice = event.highestPrice ? parseFloat(event.highestPrice.replace(/[^0-9.]/g, '')) : 0;

    return {
        actualPrice: lowestPrice || highestPrice || 0,
        discountPrice: (lowestPrice && highestPrice && lowestPrice < highestPrice) ? highestPrice : undefined,
        currencyCode: 'USD' as CurrencyCode // Default currency, could be extracted from price format if needed
    };
}

